import type { ReactNode } from "react";
import type { RowData } from "@tanstack/react-table";
import type { DataGridCellProps } from "../../cells/types";
import { COLUMN_KIND_DEFAULTS } from "../../columns/columnKinds";
import type { DataGridListEditor } from "../../columns/listEditors";
import styles from "./ListEditCell.module.scss";
import { useListCellState } from "./listCellContext";

interface ListEditCellProps<
    TData extends RowData,
> extends DataGridCellProps<TData> {
    editor: DataGridListEditor<TData>;
    /** The column's normal display for the row. */
    display: ReactNode;
}

/**
 * The cell of a column with `edit`: its editor where the row allows it, the
 * display with the read-only reason as its tooltip where it doesn't, and the
 * plain display outside list tables.
 */
export const ListEditCell = <TData extends RowData>({
    context,
    meta,
    editor,
    display,
}: Readonly<ListEditCellProps<TData>>) => {
    const state = useListCellState();
    if (state === null) {
        return display;
    }
    if (state.access === "readOnly") {
        return state.readOnlyReason === undefined ? (
            display
        ) : (
            <span
                className={styles.readOnly}
                data-cell-tooltip={state.readOnlyReason}
                data-cell-tooltip-always=""
            >
                {display}
            </span>
        );
    }
    const row = context.row.original;
    const columnLabel = meta.ariaLabel ?? meta.headerLabel;
    const isEmpty = editor.isEmpty(row);
    return (
        <editor.Editor
            row={row}
            columnLabel={columnLabel}
            trigger={{
                display,
                isEmpty,
                placeholder: editor.placeholder,
                "aria-label":
                    COLUMN_KIND_DEFAULTS[meta.kind].hasTextlessDisplay &&
                    !isEmpty
                        ? columnLabel
                        : undefined,
            }}
            isOpen={state.isOpen}
            onOpenChange={state.setOpen}
            onCloseAndCommit={state.closeAndCommit}
        />
    );
};
